export type SupportedCurrency = string;

const DEFAULT_CURRENCY: SupportedCurrency = "usd";
const DEFAULT_LOCALE = "en-US";

export type CurrencyRates = Record<SupportedCurrency, number>;

export type CurrencyMetadata = {
  locale?: string | null;
  label?: string | null;
};

export type CurrencyMetadataMap = Record<SupportedCurrency, CurrencyMetadata>;

let runtimeCurrencyMetadata: CurrencyMetadataMap = {};

export const syncCurrencyMetadata = (
  metadata: CurrencyMetadataMap | undefined
): void => {
  if (!metadata) {
    runtimeCurrencyMetadata = {};
    return;
  }

  const normalized: CurrencyMetadataMap = {};

  Object.entries(metadata).forEach(([currency, details]) => {
    const key = normalizeCurrencyCode(currency);
    if (!key) return;

    normalized[key] = {
      locale:
        typeof details?.locale === "string" && details.locale.trim()
          ? details.locale
          : undefined,
      label:
        typeof details?.label === "string" && details.label.trim()
          ? details.label
          : undefined,
    };
  });

  runtimeCurrencyMetadata = normalized;
};

const DEFAULT_RATES: CurrencyRates = {
  [DEFAULT_CURRENCY]: 1,
};

const parseAmount = (amount?: number | string | null): number => {
  if (amount === null || amount === undefined) return 0;
  const parsed = typeof amount === "string" ? Number(amount) : amount;
  return Number.isFinite(parsed) ? parsed : 0;
};

export const normalizeCurrencyCode = (
  currency?: string | null,
  fallback: SupportedCurrency = DEFAULT_CURRENCY
): SupportedCurrency => {
  if (!currency) return fallback;
  const normalized = currency.toString().trim().toLowerCase();
  return normalized || fallback;
};

const resolveCurrencyRate = (
  currency: SupportedCurrency,
  rates?: CurrencyRates
): number => {
  const normalized = normalizeCurrencyCode(currency);
  const providedRate = rates?.[normalized];
  if (typeof providedRate === "number" && Number.isFinite(providedRate)) {
    return providedRate;
  }

  return (
    DEFAULT_RATES[normalized] ??
    DEFAULT_RATES[DEFAULT_CURRENCY] ??
    DEFAULT_RATES[normalizeCurrencyCode(DEFAULT_CURRENCY)] ??
    1
  );
};

export const convertFromUSD = (
  amount: number | string | null | undefined,
  currency: SupportedCurrency,
  rates?: CurrencyRates
): number => {
  const parsedAmount = parseAmount(amount);
  const rate = resolveCurrencyRate(currency, rates);
  return parsedAmount * rate;
};

const getCurrencyMetadata = (
  currency: SupportedCurrency,
  metadata?: CurrencyMetadataMap
): CurrencyMetadata | undefined => {
  const normalized = normalizeCurrencyCode(currency);
  const map = metadata ?? runtimeCurrencyMetadata;
  return map[normalized];
};

const getCurrencyLocale = (
  currency: SupportedCurrency,
  metadata?: CurrencyMetadataMap
): string => {
  const entry = getCurrencyMetadata(currency, metadata);
  return entry?.locale ?? DEFAULT_LOCALE;
};

const getCurrencySymbol = (currency: SupportedCurrency): string | null => {
  const normalized = normalizeCurrencyCode(currency);
  const code = normalized.toUpperCase();

  try {
    const formatter = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const parts = formatter.formatToParts(0);
    const symbolPart = parts.find((part) => part.type === "currency");
    return symbolPart?.value ?? null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

const getCurrencyDisplayName = (
  currency: SupportedCurrency,
  metadata?: CurrencyMetadataMap
): string => {
  const normalized = normalizeCurrencyCode(currency);
  const metadataLabel = getCurrencyMetadata(currency, metadata)?.label;

  if (metadataLabel) {
    return metadataLabel;
  }

  const code = normalized.toUpperCase();

  try {
    if (typeof Intl.DisplayNames !== "undefined") {
      const displayNames = new Intl.DisplayNames(undefined, {
        type: "currency",
      });
      const name = displayNames.of(code);
      if (name) {
        const symbol = getCurrencySymbol(normalized);
        if (symbol && symbol !== code) {
          return `${name} (${symbol})`;
        }
        return name;
      }
    }
  } catch (error) {
    // Ignore environments that don't support Intl.DisplayNames
    console.log(error);
  }

  const fallbackSymbol = getCurrencySymbol(normalized);
  if (fallbackSymbol && fallbackSymbol !== code) {
    return `${code} (${fallbackSymbol})`;
  }
  return code;
};

export const buildCurrencyOptions = (
  currencies: SupportedCurrency[],
  metadata?: CurrencyMetadataMap
): { value: SupportedCurrency; label: string }[] => {
  const unique = Array.from(
    new Set(currencies.map((currency) => normalizeCurrencyCode(currency)))
  );

  return unique.map((value) => ({
    value,
    label: getCurrencyDisplayName(value, metadata),
  }));
};

export const formatCurrency = (
  amount: number | string | null | undefined,
  currency: SupportedCurrency,
  options?: Intl.NumberFormatOptions,
  metadata?: CurrencyMetadataMap
): string => {
  const normalizedCurrency = normalizeCurrencyCode(currency).toUpperCase();
  const locale = getCurrencyLocale(currency, metadata);
  const parsedAmount = parseAmount(amount);
  const digits = getCurrencyFractionDigits(normalizedCurrency, locale);

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: normalizedCurrency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    ...options,
  }).format(parsedAmount);
};

/**
 * The currency's own decimal places: 2 for dollars, 0 for yen. Forcing 2
 * showed ¥34,840.32 for a total Stripe charges as ¥34,840, since yen has no
 * fractional unit.
 */
export const getCurrencyFractionDigits = (
  currency: SupportedCurrency,
  locale: string = DEFAULT_LOCALE
): number => {
  try {
    return (
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency: normalizeCurrencyCode(currency).toUpperCase(),
      }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
};
